import { Alert, AlertDescription, AlertTitle } from '@invent/wl-ui-kit-next/ui';

import { ApiError } from '@/domains/api';

/**
 * How the hub's writes say what came back — the numbers' two presses, the accounts' link and
 * unlink, and a team's routing decision, archive and restore — in one rendering and one set of
 * words, so a refusal beside a number, an account or a team reads alike. Its own module because
 * several sections use it and none owns it.
 */

/** What a write was told — a refusal, loud, or what happened — with an optional heading. The
 *  hub's team writes and both Team firms tabs hold theirs in this shape. */
export interface ISaidNote {
  tone: 'done' | 'refused';
  title?: string;
  text: string;
}

/** What a write was told, beside the control that made it — a number's press, or a link's. A
 *  refusal is loud; anything else is what happened. */
export function NoteLine({ note }: { note: { tone: string; title?: string; text: string } }) {
  return (
    <Alert variant={note.tone === 'refused' ? 'destructive' : 'default'}>
      {note.title && <AlertTitle>{note.title}</AlertTitle>}
      <AlertDescription>{note.text}</AlertDescription>
    </Alert>
  );
}

/** Where a refusal says the hub is out of date, it says how to bring it up to date. */
export const OUT_OF_DATE = 'Reload the page to see what it holds now.';

/** A sentence, whatever the server's reason ended with. */
export function sentence(text: string): string {
  return /[.!?]$/.test(text) ? text : `${text}.`;
}

/** Said after a write whose re-read failed: the write stands, and the hub is as it was read. */
export const NOT_REREAD = ' The page could not be read again — reload to see it.';

/**
 * A write whose answer never arrived — the connection dropped, or survey-api failed with a 5xx —
 * may have landed or not, and "not linked" would be a claim the page cannot make: a retry of a
 * link that did land is refused 409, and of an unlink that did land 404, each then blaming a
 * hub that was merely read before the write. So the hub is read again, and the note says to
 * look at it.
 */
export function unanswered(error: unknown): boolean {
  return !(error instanceof ApiError) || error.status >= 500;
}

/** survey-api's bodiless 404 — on a figures or placements read, an unknown participant alone (D3);
 *  on their writes, also an unknown survey, metric, scope or list. */
export function isNotFound(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

/** Whether a failed write leaves the page to be read again: an answer that never arrived (the write
 *  may have landed), and a 404 — the participant is gone, or the survey's catalogue moved. */
export function rereadsAfter(error: unknown): boolean {
  return unanswered(error) || isNotFound(error);
}

/** What a write whose answer never arrived says — `change` names what may have changed. */
export function unansweredNote(read: boolean, change: string): ISaidNote {
  return {
    tone: 'refused',
    title: 'No answer',
    text: read
      ? `The answer did not arrive — the page has been read again: check whether the ${change} ` +
        'changed.'
      : `The answer did not arrive, and the page could not be read again — reload to see ` +
        `whether the ${change} changed.`
  };
}
